import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import GridSectionLoader from '../../components/CustomComponents/preLoad/gridSectionLoader';
import showErrorToast from '../../components/CustomComponents/toast/toast';
import useThemeColor from '../../hooks/useThemeColor';
import DateRangePicker from '../../components/CustomComponents/DateRangePicker/DateRangePicker';
import moment from 'moment';
import Select from 'react-select';
import useDarkMode from '../../hooks/useDarkMode';
import DropDownStyles, { DropDownStylesDark, defaultSelectPortalProps } from '../staff/dropdownStyles';
import { encodeUrlParam } from '../../utils/urlUtils';
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from '../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar';
import { getModernGridFilterPillSelectStyles } from '../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles';
import useDebouncedEffect from '../../hooks/useDebouncedEffect';
import { normalizeRosterLeaveList } from '../../utils/rosterLeaveUtils';

const AllStaffShiftReport = () => {
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user?.userData?.compID);
  const { profileData } = useSelector((state) => state.auth);
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );

  const [loading, setLoading] = useState(false);
  const [staffReports, setStaffReports] = useState([]);
  const [allStaffList, setAllStaffList] = useState([]);
  const [startDate, setStartDate] = useState(moment().startOf('week').toDate());
  const [endDate, setEndDate] = useState(moment().endOf('week').toDate());
  const [selectedStaff, setSelectedStaff] = useState({ value: '', label: 'All Staff' });
  const [search, setSearch] = useState('');
  const [compCurrency, setCompCurrency] = useState("GBP");
  const [staffLeaves, setStaffLeaves] = useState([]);

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    PKR: "₨",
    Rs: "₨", // Handle legacy "Rs" code
    // Add more currency symbols as needed
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Normalize currency code (handle legacy "Rs" -> "PKR")
  const normalizeCurrencyCode = (currencyCode) => {
    if (currencyCode === "Rs") return "PKR";
    return currencyCode || "GBP";
  };

  useEffect(() => {
    // Fetch company currency from profile data
    if (profileData?.compCurrency) {
      const normalizedCurrency = normalizeCurrencyCode(profileData.compCurrency);
      setCompCurrency(normalizedCurrency);
    }
  }, [profileData]);

  useEffect(() => {
    fetchStaffList();
  }, []);

  useDebouncedEffect(() => {
    fetchStaffReports();
    fetchStaffLeaves();
  }, [startDate, endDate, selectedStaff, search]);

  const fetchStaffList = async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
        params: { 
          CompID,
          SortBy: 'First_Name',
          SortOrder: 'ASC',
          Search: '',
          PageSize: 1000,
          PageNumber: 1
        }
      });
      
      console.log('Staff List API Response:', response.data); // Debug log
      
      const staffData = response.data.data || response.data || [];
      console.log('Staff List Data:', staffData); // Debug log
      
      if (Array.isArray(staffData)) {
        console.log('Setting staff list, count:', staffData.length); // Debug log
        setAllStaffList(staffData);
      } else {
        console.log('Staff data is not an array'); // Debug log
        setAllStaffList([]);
      }
    } catch (err) {
      showErrorToast('Failed to fetch staff list');
      console.error('Error fetching staff list:', err);
      setAllStaffList([]);
    }
  };

  const fetchStaffLeaves = async () => {
    if (!CompID) return;

    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_ROSTER_LEAVE_DAYS, {
        params: {
          CompID,
          UserName: selectedStaff?.value || null,
          StartDate: startDate && moment(startDate).isValid() ? moment(startDate).format('YYYY-MM-DD') : null,
          EndDate: endDate && moment(endDate).isValid() ? moment(endDate).format('YYYY-MM-DD') : null,
        },
      });

      const data = response.data?.data || response.data || [];
      const normalizedLeaves = normalizeRosterLeaveList(data);

      setStaffLeaves(normalizedLeaves);
    } catch (error) {
      console.error('Error fetching staff leaves:', error);
      setStaffLeaves([]);
    }
  };

  const fetchStaffReports = async () => {
    if (!CompID) return;

    setLoading(true);
    try {
      // Build params object, only include userName if it has a value
      const params = {
        compID: CompID,
        startDate: startDate && moment(startDate).isValid() ? moment(startDate).format('YYYY-MM-DD') : '',
        endDate: endDate && moment(endDate).isValid() ? moment(endDate).format('YYYY-MM-DD') : '',
        userName: selectedStaff?.value || ''
      };

       
      const response = await axiosInstance.get(APIs.GET_ALL_STAFF_WORK_REPORTS, {
        params: params
      });

      if (response.data && response.data.staffReports) {
        console.log('Staff Reports Response:', response.data.staffReports); // Debug log
        
        let reports = response.data.staffReports.map(staff => {
          console.log('Processing staff (raw):', staff); // Debug log
          
          // ASP.NET Core returns camelCase by default
          const mappedStaff = {
            staff: {
              UserName: staff.userName || staff.UserName || '',
              FirstName: staff.firstName || staff.FirstName || '',
              LastName: staff.lastName || staff.LastName || '',
              Email: staff.email || staff.Email || '',
              RoleName: staff.roleName || staff.RoleName || '',
              UserImage: staff.userImage || staff.UserImage || null,
              IsSalaryStaff: staff.isSalaryStaff || staff.IsSalaryStaff || false,
              SalaryType: staff.salaryType || staff.SalaryType || '',
              Salary: staff.salary || staff.Salary || 0,
              RatePerHour: staff.ratePerHour || staff.RatePerHour || 0
            },
            report: {
              WeekStats: {
                DaysWorked: staff.daysWorked || staff.DaysWorked || 0,
                RegularHours: staff.regularHours || staff.RegularHours || 0,
                OvertimeHours: staff.overtimeHours || staff.OvertimeHours || 0,
                TotalBreakHours: staff.totalBreakHours || staff.TotalBreakHours || 0,
                ExtraBreakHours: staff.extraBreakHours || staff.ExtraBreakHours || 0,
                UnpaidBreakHours: staff.unpaidBreakHours || staff.UnpaidBreakHours || 0,
                TotalHours: staff.totalHours || staff.TotalHours || 0,
                TotalEarnings: staff.totalEarnings || staff.TotalEarnings || 0
              },
              EntryCount: staff.entryCount || staff.EntryCount || 0
            }
          };
          
          console.log('Mapped staff:', mappedStaff.staff); // Debug log
          return mappedStaff;
        });

        // Apply search filter on frontend if needed
        if (search.trim()) {
          reports = reports.filter(({ staff }) => 
            (staff.FirstName || staff.firstName || '')?.toLowerCase().includes(search.toLowerCase()) ||
            (staff.LastName || staff.lastName || '')?.toLowerCase().includes(search.toLowerCase()) ||
            (staff.Email || staff.email || '')?.toLowerCase().includes(search.toLowerCase())
          );
        }

        setStaffReports(reports);
      } else {
        setStaffReports([]);
      }
    } catch (err) {
      showErrorToast('Failed to fetch staff reports');
      console.error('Error fetching staff reports:', err);
      setStaffReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = update;
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    } else {
      showErrorToast('Invalid date selected');
    }
  };

  // Get leaves for a specific staff member
  const getStaffLeaves = (userName) => {
    if (!userName || !staffLeaves.length) return [];
    return staffLeaves.filter(
      (leave) => (leave.UserName || '').toLowerCase() === userName.toLowerCase()
    );
  };

  // Calculate total leave duration in hours for a staff member
  const getTotalLeaveHours = (userName) => {
    const leaves = getStaffLeaves(userName);
    return leaves.reduce((total, leave) => total + (leave.TotalHours || 0), 0);
  };

  // Check if staff has any paid leaves
  const hasPaidLeaves = (userName) => {
    const leaves = getStaffLeaves(userName);
    return leaves.some((leave) => leave.IsPaid === true);
  };

  // Check if staff has any unpaid leaves
  const hasUnpaidLeaves = (userName) => {
    const leaves = getStaffLeaves(userName);
    return leaves.some((leave) => leave.IsPaid === false);
  };

  const handleStaffChange = (selectedOption) => {
    setSelectedStaff(selectedOption);
  };

  const handleViewDetails = (userName) => {
    const queryParams = new URLSearchParams();
    if (startDate && moment(startDate).isValid()) {
      queryParams.append('startDate', moment(startDate).format('YYYY-MM-DD'));
    }
    if (endDate && moment(endDate).isValid()) {
      queryParams.append('endDate', moment(endDate).format('YYYY-MM-DD'));
    }
    navigate(`/staff/StaffShiftReport/${encodeUrlParam(userName)}?${queryParams.toString()}`);
  };

  const formatCurrency = (amount) => {
    try {
      // Use dynamic currency from company settings
      const normalizedCurrency = normalizeCurrencyCode(compCurrency);
      const locale = normalizedCurrency === 'USD' ? 'en-US' 
                   : normalizedCurrency === 'EUR' ? 'en-EU' 
                   : normalizedCurrency === 'PKR' ? 'en-PK'
                   : 'en-GB';
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: normalizedCurrency
      }).format(amount || 0);
    } catch (error) {
      // Fallback to simple formatting with symbol if currency code is invalid
      console.error('Currency formatting error:', error);
      const symbol = getCurrencySymbol(compCurrency);
      return `${symbol}${(amount || 0).toFixed(2)}`;
    }
  };

  const staffOptions = React.useMemo(() => {
    console.log('Building staff options from:', allStaffList); // Debug log
    
    const options = [
      { value: '', label: 'All Staff' },
      ...allStaffList.map(staff => {
        console.log('Mapping staff:', staff); // Debug each staff member
        return {
          value: staff.userName || staff.UserName,
          label: `${staff.firstName || ''} ${staff.lastName || ''}`.trim() || staff.email || staff.userName || 'Unknown'
        };
      })
    ];
    
    console.log('Final staff options:', options); // Debug log
    return options;
  }, [allStaffList]);

  const totalSummary = staffReports.reduce((acc, { report }) => {
    if (report?.WeekStats) {
      const daysWorked = report.WeekStats.DaysWorked || 0;
      const totalBreakHours = report.WeekStats.TotalBreakHours || 0;
      const extraBreakHours = report.WeekStats.ExtraBreakHours || 0;
      
      acc.totalHours += report.WeekStats.TotalHours || 0;
      acc.regularHours += report.WeekStats.RegularHours || 0;
      acc.overtimeHours += report.WeekStats.OvertimeHours || 0;
      acc.totalBreakHours += totalBreakHours;
      acc.extraBreakHours += extraBreakHours;
      acc.totalEarnings += report.WeekStats.TotalEarnings || 0;
      acc.daysWorked += daysWorked;
    }
    return acc;
  }, { totalHours: 0, regularHours: 0, overtimeHours: 0, totalBreakHours: 0, extraBreakHours: 0, totalEarnings: 0, daysWorked: 0 });

  // Calculate total leave hours across all staff
  const totalLeaveHours = staffLeaves.reduce((total, leave) => total + (leave.TotalHours || 0), 0);
  const paidLeaveHours = staffLeaves.filter(l => l.IsPaid).reduce((total, leave) => total + (leave.TotalHours || 0), 0);
  const unpaidLeaveHours = staffLeaves.filter(l => !l.IsPaid).reduce((total, leave) => total + (leave.TotalHours || 0), 0);

  return (
    <div>
      {/* Breadcrumb */}
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><a href="#/">Home</a></li>
        <li className="breadcrumb-item"><a href="#/report">Reports</a></li>
        <li className="breadcrumb-item active">Staff Shift Report</li>
      </ol>
      <h1 className="page-header">Staff Shift Report</h1>

      <div className="mb-4" style={{ position: 'relative', zIndex: 10 }}>
        <ModernGridToolbar
          isDarkMode={isDarkMode}
          title="Staff Shift Report"
          totalCount={staffReports.length}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search staff..."
          topFilters={
            <>
              <ModernGridToolbarFilterPill iconClass="fa fa-calendar" wide>
                <DateRangePicker
                  startDate={startDate && moment(startDate).isValid() ? startDate : null}
                  endDate={endDate && moment(endDate).isValid() ? endDate : null}
                  onChange={handleDateRangeChange}
                  placeholder="Select Date Range"
                />
              </ModernGridToolbarFilterPill>
              <ModernGridToolbarFilterPill iconClass="fa fa-user">
                <Select
                  value={selectedStaff}
                  onChange={handleStaffChange}
                  options={staffOptions}
                  styles={filterPillSelectStyles}
                  placeholder="All Staff"
                  isSearchable
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
            </>
          }
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="320px">
      {/* Summary Cards - Compact Single Row Design */}
      <div className="mb-4" style={{ position: 'relative', zIndex: 1 }}>
        <div className="d-flex gap-3 flex-wrap">
          {/* Total Staff */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-users" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{staffReports.length}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Staff</div>
            </div>
          </div>

          {/* Total Hours */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-clock" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{totalSummary.totalHours.toFixed(1)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Hours</div>
            </div>
          </div>

          {/* Overtime Hours */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-arrow-trend-up" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{totalSummary.overtimeHours.toFixed(1)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Overtime Hours</div>
            </div>
          </div>

          {/* Break Hours */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-coffee" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{totalSummary.totalBreakHours.toFixed(1)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Break Hours</div>
            </div>
          </div>

          {/* Extra Break Hours */}
          {totalSummary.extraBreakHours > 0 && (
            <div 
              className="flex-grow-1" 
              style={{ 
                minWidth: '150px',
                backgroundColor: '#dc3545',
                borderRadius: '8px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: 'white'
              }}
            >
              <i className="fa fa-exclamation-triangle" style={{ fontSize: '24px', opacity: 0.9 }}></i>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{totalSummary.extraBreakHours.toFixed(1)}</div>
                <div style={{ fontSize: '12px', opacity: 0.85 }}>Extra Break</div>
              </div>
            </div>
          )}

          {/* Total Earnings */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-money-bill-wave" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(totalSummary.totalEarnings)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Earnings</div>
            </div>
          </div>

          {/* Total Leave Hours */}
          {totalLeaveHours > 0 && (
            <div 
              className="flex-grow-1" 
              style={{ 
                minWidth: '150px',
                backgroundColor: '#6c757d',
                borderRadius: '8px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: 'white'
              }}
            >
              <i className="fa fa-calendar-times" style={{ fontSize: '24px', opacity: 0.9 }}></i>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{totalLeaveHours.toFixed(1)}</div>
                <div style={{ fontSize: '12px', opacity: 0.85 }}>
                  Leave Hours
                  {paidLeaveHours > 0 && unpaidLeaveHours > 0 && (
                    <div style={{ fontSize: '10px', opacity: 0.75 }}>
                      {paidLeaveHours.toFixed(1)} paid, {unpaidLeaveHours.toFixed(1)} unpaid
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Staff Report Table */}
      <div className="card border-0" style={{ position: 'relative', zIndex: 1 }}>
        <div className="card-body p-0">
          <div className="table-responsive" style={{ position: 'relative', zIndex: 1 }}>
            <table className="table table-hover mb-0">
              <thead 
                className="bg-light" 
                style={{ 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 3,
                  backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa'
                }}
              >
                <tr>
                  <th className="pt-3 pb-3">Staff Member</th>
                  <th className="pt-3 pb-3 text-center">Days Worked</th>
                  <th className="pt-3 pb-3 text-center">Total Hours</th>
                  <th className="pt-3 pb-3 text-center">Overtime Hours</th>
                  <th className="pt-3 pb-3 text-center">Break Hours</th>
                  <th className="pt-3 pb-3 text-center">Extra Break</th>
                  <th className="pt-3 pb-3 text-center">Leaves</th>
                  <th className="pt-3 pb-3 text-center">Salary/Rate</th>
                  <th className="pt-3 pb-3 text-end">Total Earnings</th>
                  <th className="pt-3 pb-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {staffReports.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-5">
                      <i className="fa fa-users fa-3x text-muted mb-3 d-block"></i>
                      <h5>No Staff Shift Reports Found</h5>
                      <p className="text-muted">Try adjusting your filters</p>
                    </td>
                  </tr>
                ) : (
                  staffReports.map(({ staff, report }) => {
                    console.log('Rendering staff row:', staff); // Debug log
                    
                    // Calculate average hourly rate (excluding break hours)
                    const regularHours = report?.WeekStats?.RegularHours || 0;
                    const overtimeHours = report?.WeekStats?.OvertimeHours || 0;
                    const workingHours = regularHours + overtimeHours; // Excludes break hours
                    const totalEarnings = report?.WeekStats?.TotalEarnings || 0;
                    const avgHourlyRate = workingHours > 0 ? (totalEarnings / workingHours) : 0;
                    
                    // Calculate extra break hours (Total break - Allowed break)
                    const extraBreakHours = report?.WeekStats?.ExtraBreakHours || 0;
                    
                    // Get leave information for this staff member
                    const staffUserName = staff.UserName || staff.userName;
                    const leaves = getStaffLeaves(staffUserName);
                    const totalLeaveHours = getTotalLeaveHours(staffUserName);
                    const hasPaid = hasPaidLeaves(staffUserName);
                    const hasUnpaid = hasUnpaidLeaves(staffUserName);
                    
                    return (
                    <tr key={staffUserName}>
                      <td className="align-middle">
                        <div className="d-flex align-items-center">
                          <div className="w-40px h-40px rounded-circle bg-light d-flex align-items-center justify-content-center me-3">
                            <i className="fa fa-user text-muted"></i>
                          </div>
                          <div>
                            <div className="fw-bold">
                              {staff.FirstName && staff.LastName 
                                ? `${staff.FirstName} ${staff.LastName}` 
                                : staff.firstName && staff.lastName
                                ? `${staff.firstName} ${staff.lastName}`
                                : staffUserName || 'N/A'}
                            </div>
                            <div className="text-muted small">{staff.RoleName || staff.roleName || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="align-middle text-center">
                        <span className="badge bg-info">
                          {report?.WeekStats?.DaysWorked || 0}
                        </span>
                      </td>
                      <td className="align-middle text-center">
                        {report?.WeekStats?.TotalHours?.toFixed(1) || '0.0'} hrs
                      </td>
                      <td className="align-middle text-center">
                        {report?.WeekStats?.OvertimeHours?.toFixed(1) || '0.0'} hrs
                      </td>
                      <td className="align-middle text-center">
                        <span className={report?.WeekStats?.TotalBreakHours > 0 ? 'text-warning fw-semibold' : ''}>
                          {report?.WeekStats?.TotalBreakHours?.toFixed(1) || '0.0'} hrs
                        </span>
                      </td>
                      <td className="align-middle text-center">
                        <span className={extraBreakHours > 0 ? 'text-danger fw-bold' : 'text-muted'}>
                          {extraBreakHours.toFixed(1)} hrs
                        </span>
                      </td>
                      <td className="align-middle text-center">
                        {leaves.length > 0 ? (
                          <div className="d-flex flex-column align-items-center gap-1">
                            <div>
                              <span className="badge bg-primary">
                                <i className="fa fa-calendar-times me-1"></i>
                                {totalLeaveHours.toFixed(1)} hrs
                              </span>
                            </div>
                            <div className="d-flex gap-1 justify-content-center flex-wrap">
                              {hasPaid && (
                                <span className="badge bg-success" title="Has Paid Leave">
                                  <i className="fa fa-check-circle me-1"></i>Paid
                                </span>
                              )}
                              {hasUnpaid && (
                                <span className="badge bg-secondary" title="Has Unpaid Leave">
                                  <i className="fa fa-times-circle me-1"></i>Unpaid
                                </span>
                              )}
                            </div>
                            <div>
                              <small className="text-muted d-block">
                                {leaves.length} leave{leaves.length !== 1 ? 's' : ''}
                              </small>
                            </div>
                            {leaves.length > 0 && (
                              <div 
                                className="text-primary cursor-pointer"
                                title={leaves.map((leave) => 
                                  `${leave.LeaveType || 'Leave'}: ${moment(leave.StartDate).format('MMM DD, YYYY')} - ${moment(leave.EndDate).format('MMM DD, YYYY')} (${leave.IsPaid ? 'Paid' : 'Unpaid'}) - ${(leave.TotalHours || 0).toFixed(1)} hrs`
                                ).join(' | ')}
                                style={{ cursor: 'pointer' }}
                              >
                                <i className="fa fa-info-circle" style={{ fontSize: '12px' }}></i>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="align-middle text-center">
                        {(() => {
                          const isSalaryStaff = staff.IsSalaryStaff || staff.isSalaryStaff;
                          const salaryType = staff.SalaryType || staff.salaryType || '';
                          const salary = staff.Salary || staff.salary || 0;
                          const ratePerHour = staff.RatePerHour || staff.ratePerHour || 0;

                          if (isSalaryStaff) {
                            // For salary staff, show the fixed salary with type
                            if (salaryType === 'Monthly') {
                              return (
                                <div>
                                  <div className="text-success fw-semibold">{formatCurrency(salary)}</div>
                                  <div className="text-muted small">Monthly</div>
                                </div>
                              );
                            } else if (salaryType === 'Weekly') {
                              return (
                                <div>
                                  <div className="text-success fw-semibold">{formatCurrency(salary)}</div>
                                  <div className="text-muted small">Weekly</div>
                                </div>
                              );
                            }
                          } else {
                            // For hourly staff, show hourly rate
                            return (
                              <div>
                                <div className="text-info fw-semibold">{formatCurrency(ratePerHour)}/hr</div>
                                <div className="text-muted small">Hourly</div>
                              </div>
                            );
                          }
                          return <span className="text-muted">-</span>;
                        })()}
                      </td>
                      <td className="align-middle text-end fw-bold">
                        {formatCurrency(report?.WeekStats?.TotalEarnings || 0)}
                      </td>
                      <td className="align-middle text-center">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleViewDetails(staff.UserName || staff.userName)}
                          title="View Detailed Report"
                        >
                          <i className="fa fa-eye me-1"></i>
                          View Details
                        </button>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </GridSectionLoader>
    </div>
  );
};

export default AllStaffShiftReport;
